import { Auth } from 'aws-amplify';
import moment from 'moment';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import {
  getCurrentBehaviourForecast,
  getGridCampaigns,
  getHistoricSystemData,
  getTwentyHoursOfSystemData,
  getUserDetails,
  tdasCampaignId,
} from '../api/Optimise';
import { checkSubscriptionExpired, isTrialSubscription } from '../api/Subscription';
import { AppContext } from '../context/AppContext';
import { useActiveSubscription } from '../hooks';
import AppCard from '../ui/AppCard';
import AppLayout from '../ui/Layout/AppLayout';
import DatePickerLayout from '../ui/Layout/DatePickerLayout';
import EnergyFlowSavingLayoutContainer from '../ui/Layout/EnergyFlowSavingLayoutContainer';
import { DisconnectAlert, EventAlert } from '../ui/Styled/AlertBarPreset';
import SubscriptionExpiredAlert from '../ui/Styled/AlertBarPreset/SubscriptionExpiredAlert';
import { hasLastTwoSocReadings, isForecastStale } from '../utils/disconnectHelpers';
import getCampaignById from '../utils/getCampaignById';
import getLoopIsDisabled from '../utils/getLoopIsDisabled';

import {
  AdminContainer,
  EnergyFlowContainer,
  HelpContainer,
  NotFoundContainer,
  SavingsContainer,
  SettingsContainer,
  TariffRecommenderContainer,
  TodayContainer,
} from './App';

const DatePickerRoutes = () => (
  <DatePickerLayout>
    <Switch>
      <Route path="/app/savings/:timestamp?" component={SavingsContainer} exact />
      <Route path="/app/energy-flow/:timestamp?" component={EnergyFlowContainer} exact />
    </Switch>
  </DatePickerLayout>
);

const EnergyFlowSavingWrapper = () => (
  <EnergyFlowSavingLayoutContainer>
    <DatePickerRoutes />
  </EnergyFlowSavingLayoutContainer>
);

const AppContainer = () => {
  const { activeSubscription, fetchedSubscriptions } = useActiveSubscription();
  const history = useHistory();
  const {
    userSub,
    isAdmin,
    setIsAdmin,
    campaigns,
    setCampaigns,
    setTdasCampaign,
    setTdasIsActive,
    gridEvent,
    twentyFourHoursOfData,
    setAnalyticsStartDate,
    showSimulationMode,
    setUserDetails,
    userDetails,
    setTwentyFourHoursOfData,
    setLatestRecord,
    fetchingTwentyFourHoursOfData,
    setFetchingTwentyFourHoursOfData,
    isOnboarded,
    subscriptionsDisabled,
  } = useContext(AppContext);

  /**
   * Container state
   */
  const [fetchingBehaviourForecast, setFetchingBehaviorForecast] = useState(false);
  const [forecastIsStaleError, setForecastIsStaleError] = useState(false);
  const [socReadingsMissing, setSocReadingsMissing] = useState(false);

  /**
   * Memoized values
   */
  const inverterDisconnectError = useMemo(
    () => !fetchingTwentyFourHoursOfData && !fetchingBehaviourForecast && (forecastIsStaleError || socReadingsMissing),
    [fetchingTwentyFourHoursOfData, fetchingBehaviourForecast, forecastIsStaleError, socReadingsMissing]
  );
  const loopIsDisabled = useMemo(
    () => getLoopIsDisabled(showSimulationMode, userDetails),
    [showSimulationMode, userDetails]
  );
  const subscriptionHasExpired = useMemo(
    () => checkSubscriptionExpired(activeSubscription?.renewal_date),
    [activeSubscription?.renewal_date]
  );
  const showSubscriptionExpiredAlert = useMemo(
    () => !subscriptionsDisabled && subscriptionHasExpired,
    [subscriptionsDisabled, subscriptionHasExpired]
  );

  // Only compute/emit the trial flag once subscriptions have been fetched,
  // so we can default to hiding the pre-grace banner (prevents flashing).
  const isTrial = useMemo(
    () => (fetchedSubscriptions ? isTrialSubscription(activeSubscription) : undefined),
    [fetchedSubscriptions, activeSubscription]
  );

  /**
   * Side effects
   */
  useEffect(() => {
    if (!isOnboarded) {
      history.replace('/onboarding');
    }
  }, [isOnboarded, history]);

  /**
   * Check if the user has a recent behaviour forecast and show an error banner if not
   */
  useEffect(() => {
    const fetchBehaviourForecast = async () => {
      setFetchingBehaviorForecast(true);
      try {
        const forecast = await getCurrentBehaviourForecast(userSub);
        setForecastIsStaleError(isForecastStale(forecast));
      } catch (error) {
        console.error('Error fetching current behaviour forecast:', error);
        setForecastIsStaleError(false);
      }
      setFetchingBehaviorForecast(false);
    };
    fetchBehaviourForecast();
  }, [userSub]);

  /**
   * System data retrieval and processing
   */
  useEffect(() => {
    const asyncUseEffect = async () => {
      setFetchingTwentyFourHoursOfData(true);
      try {
        let data = [];
        if (!loopIsDisabled) {
          data = await getTwentyHoursOfSystemData(userSub).catch((error) =>
            console.error('Error fetching historic system data:', error)
          );
        } else {
          const now = moment().unix();
          const twentyFourHoursAgo = moment().subtract(24, 'hours').unix();
          data = await getHistoricSystemData(twentyFourHoursAgo, now, userSub).catch((error) =>
            console.error('Error fetching historic system data:', error)
          );
        }
        if (!!data) {
          const latestRecord = data[data?.length - 1];
          setLatestRecord(latestRecord);
        }
        setTwentyFourHoursOfData(data);
        setFetchingTwentyFourHoursOfData(false);
      } catch (error) {
        setFetchingTwentyFourHoursOfData(false);
        console.error(error);
      }

      try {
        const user = await getUserDetails(userSub);
        setUserDetails(user);
        if (user?.plant_data_range.start_datetime) {
          setAnalyticsStartDate(user?.plant_data_range.start_datetime);
        } else {
          setAnalyticsStartDate(null);
        }
      } catch (error) {
        console.error(error);
        setAnalyticsStartDate(null);
      }
    };
    asyncUseEffect();
  }, [
    loopIsDisabled,
    setAnalyticsStartDate,
    setFetchingTwentyFourHoursOfData,
    setLatestRecord,
    setTwentyFourHoursOfData,
    setUserDetails,
    userSub,
  ]);

  /**
   * Check if the inverter is disconnected
   */
  useEffect(() => {
    // we need to check if we have the last two SOC readings
    if (!!twentyFourHoursOfData?.length) {
      setSocReadingsMissing(!hasLastTwoSocReadings(twentyFourHoursOfData));
    } else {
      setSocReadingsMissing(false);
    }
  }, [twentyFourHoursOfData]);

  /**
   * Check if the user is an admin
   */
  useEffect(() => {
    const asyncUseEffect = async () => {
      const user = await Auth.currentAuthenticatedUser();
      if (!!user?.attributes && 'custom:admin_secret' in user.attributes) {
        setIsAdmin(true);
        if (!userSub) {
          history.push('/app/admin');
        }
      }
    };
    asyncUseEffect();
  }, [history, setIsAdmin, userSub]);

  /**
   * Grab all TDAS campaigns
   */
  useEffect(() => {
    getGridCampaigns()
      .then((campaigns) => setCampaigns(campaigns))
      .catch((error) => console.error(error));
  }, [setCampaigns]);

  /**
   * Check if turn down and save campaign is active
   */
  useEffect(() => {
    if (campaigns?.length) {
      const tdasCampaign = getCampaignById(campaigns, tdasCampaignId);
      setTdasCampaign(tdasCampaign);
      setTdasIsActive(tdasCampaign?.is_active);
    }
  }, [campaigns, setTdasCampaign, setTdasIsActive]);

  return (
    <>
      {!isAdmin && showSubscriptionExpiredAlert && (
        <SubscriptionExpiredAlert renewalDate={activeSubscription?.renewal_date} isTrial={isTrial} />
      )}
      {!showSubscriptionExpiredAlert && inverterDisconnectError && (
        <DisconnectAlert forecastIsStaleError={forecastIsStaleError} />
      )}
      {gridEvent && <EventAlert gridEvent={gridEvent} />}
      <AppLayout hideSupport>
        <Switch>
          {isAdmin && <Route path="/app/admin" component={AdminContainer} exact />}
          <Route path="/app/*">
            <AppCard>
              <Switch>
                <Route path="/app/today" component={TodayContainer} />
                <Route path={['/app/savings', '/app/energy-flow']} component={EnergyFlowSavingWrapper} />
                <Route path={'/app/tariffs'} component={TariffRecommenderContainer} />
                <Route path="/app/settings" component={SettingsContainer} exact />
                <Route path="/app/help" component={HelpContainer} exact />
                <Route path="/app/not-found" component={NotFoundContainer} />
                <Route>
                  <Redirect to="/app/not-found" />
                </Route>
              </Switch>
            </AppCard>
          </Route>
          <Route path="/app" exact>
            <Redirect to="/app/today" />
          </Route>
        </Switch>
      </AppLayout>
    </>
  );
};

export default AppContainer;
